import React, { useRef, useState } from 'react';
import axios from 'axios';
import { ImagePlus, Loader2, SlidersHorizontal, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { ArchiveTreeCover } from './ArchiveTreeCover';
import { CoverAdjustPanel } from './CoverAdjustPanel';
import { humanizeError } from '../../utils/errorHandling';
import { getApiBase } from '../../utils/apiBase';
import {
  TREE_COVER_SETTINGS_GUIDANCE,
  TREE_COVER_SETTINGS_PRIVACY,
  uploadTreeCover,
  validateTreeCoverFile,
  CUSTOMER_IMAGE_ACCEPT,
} from '../../utils/treeCoverUpload';

const API = getApiBase();

/**
 * Upload / replace / remove tree archive cover (editor+ only).
 */
export function TreeCoverManager({
  treeId,
  treeName,
  coverImageUrl,
  coverDisplay,
  coverImageWidth,
  coverImageHeight,
  canEdit,
  onUpdated,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);

  const emitPatch = (patch) => onUpdated?.(patch);

  if (!canEdit) {
    return (
      <div className="space-y-3" data-testid="tree-cover-viewer">
        <ArchiveTreeCover
          coverImageUrl={coverImageUrl}
          coverDisplay={coverDisplay}
          alt={treeName ? `${treeName} archive cover` : 'Archive cover'}
          variant="hero"
          className="rounded-2xl"
        />
        <p className="text-sm text-muted-foreground">{TREE_COVER_SETTINGS_PRIVACY}</p>
      </div>
    );
  }

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = validateTreeCoverFile(file);
    if (!validation.ok) {
      toast.error(validation.message);
      return;
    }

    setUploading(true);
    try {
      const result = await uploadTreeCover(treeId, file);
      toast.success('Archive cover saved.');
      emitPatch(result);
      setShowAdjust(true);
    } catch (error) {
      toast.error(humanizeError(error) || 'Unable to save the cover image.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!coverImageUrl) return;
    if (!window.confirm('Remove this cover? Your archive will use the default heritage banner.')) {
      return;
    }
    setRemoving(true);
    try {
      await axios.delete(`${API}/trees/${treeId}/cover`);
      toast.success('Archive cover removed.');
      emitPatch({
        cover_image_url: null,
        cover_display: null,
        cover_image_width: null,
        cover_image_height: null,
      });
      setShowAdjust(false);
    } catch (error) {
      toast.error(humanizeError(error) || 'Unable to remove the cover.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-[hsl(38_14%_86%)] bg-[hsl(42_26%_99%)]/95 p-5 shadow-[inset_0_1px_0_hsl(42_40%_100%/0.45)]"
      data-testid="tree-cover-manager"
    >
      <div className="mb-4 space-y-1.5">
        <h3 className="font-serif text-lg font-medium text-[hsl(24_12%_22%)]">Archive cover</h3>
        <p className="text-sm leading-relaxed text-muted-foreground">{TREE_COVER_SETTINGS_PRIVACY}</p>
        <p className="text-xs leading-relaxed text-muted-foreground/90">{TREE_COVER_SETTINGS_GUIDANCE}</p>
      </div>

      <ArchiveTreeCover
        coverImageUrl={coverImageUrl}
        coverDisplay={coverDisplay}
        alt={treeName ? `${treeName} archive cover` : 'Archive cover'}
        variant="hero"
        className="mb-4 rounded-2xl overflow-hidden"
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <input
          ref={fileInputRef}
          type="file"
          accept={CUSTOMER_IMAGE_ACCEPT}
          className="hidden"
          onChange={handleFile}
          data-testid="tree-cover-file-input"
        />
        <Button
          type="button"
          size="sm"
          className="rounded-full bg-[#1c1917] text-[#faf7f2] hover:bg-[#2c2824] w-full sm:w-auto"
          disabled={uploading || removing}
          onClick={() => fileInputRef.current?.click()}
          data-testid="tree-cover-upload-btn"
        >
          {uploading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
          ) : (
            <ImagePlus className="mr-2 h-4 w-4" aria-hidden />
          )}
          {coverImageUrl ? 'Replace cover' : 'Upload cover'}
        </Button>
        {coverImageUrl ? (
          <>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full border-[hsl(38_14%_82%)] text-[#5c4a32] w-full sm:w-auto"
              disabled={uploading || removing}
              onClick={() => setShowAdjust((v) => !v)}
              data-testid="tree-cover-adjust-btn"
            >
              <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden />
              {showAdjust ? 'Hide adjust' : 'Adjust cover'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-full border-[hsl(38_14%_82%)] text-[#5c4a32] w-full sm:w-auto"
              disabled={uploading || removing}
              onClick={handleRemove}
              data-testid="tree-cover-remove-btn"
            >
              {removing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" aria-hidden />
              )}
              Remove cover
            </Button>
          </>
        ) : null}
      </div>

      {coverImageUrl && showAdjust ? (
        <CoverAdjustPanel
          treeId={treeId}
          coverImageUrl={coverImageUrl}
          coverDisplay={coverDisplay}
          coverImageWidth={coverImageWidth}
          coverImageHeight={coverImageHeight}
          onSaved={(display) => emitPatch({ cover_display: display })}
        />
      ) : null}
    </div>
  );
}
